import React from "react";
import { Award, CalendarClock, Hourglass } from "lucide-react";
import { cn } from "../../ui";
import { formatDue, isDueSoon, relativeTime } from "./assignmentRules";

/**
 * The facts of a batch assignment, one line each: when it is due (and how long
 * from now), what it is out of, and whether late work is taken. The grader and
 * the student read the same line, so they never disagree about a deadline.
 *
 * `deadline` is the server's — for a date-only due date that is the end of the
 * day — never the raw `dueDate`.
 */
const AssignmentFacts = ({ deadline, dateOnly, maxMarks, lateAllowed, closed = false, className = "" }) => {
  const now = new Date();
  const past = deadline && new Date(deadline) < now;
  const soon = !closed && isDueSoon(deadline, now);
  return (
    <dl className={cn("tw-m-0 tw-flex tw-flex-wrap tw-gap-x-6 tw-gap-y-2 tw-text-[0.8125rem]", className)}>
      <div className="tw-flex tw-items-center tw-gap-1.5">
        <CalendarClock size={16} className={soon ? "tw-text-warning" : "tw-text-content-subtle"} aria-hidden="true" />
        <dt className="tw-text-content-muted">{past ? "Was due" : "Due"}</dt>
        <dd className="tw-m-0 tw-font-medium tw-text-content">
          {deadline ? formatDue(deadline, { dateOnly, now }) : "No due date"}
          {deadline && (
            <span className={cn("tw-ml-1 tw-font-normal", soon ? "tw-text-warning-fg" : "tw-text-content-subtle")}>
              ({relativeTime(deadline, now)})
            </span>
          )}
        </dd>
      </div>
      <div className="tw-flex tw-items-center tw-gap-1.5">
        <Award size={16} className="tw-text-content-subtle" aria-hidden="true" />
        <dt className="tw-text-content-muted">Out of</dt>
        <dd className="tw-m-0 tw-font-medium tw-tabular-nums tw-text-content">{maxMarks} marks</dd>
      </div>
      <div className="tw-flex tw-items-center tw-gap-1.5">
        <Hourglass size={16} className="tw-text-content-subtle" aria-hidden="true" />
        <dt className="tw-text-content-muted">Late work</dt>
        <dd className="tw-m-0 tw-font-medium tw-text-content">{lateAllowed ? "Accepted, marked late" : "Not accepted"}</dd>
      </div>
    </dl>
  );
};

export default AssignmentFacts;
